import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import styled from 'styled-components';
import { toast } from 'react-toastify';

import {
    GetDetailedOrderDocument,
    type DetailedOrderFragmentFragment,
    useGetOrderCatalogueCategoriesQuery,
    useGetOrderCatalogueQuery,
    useUpdateOrderProductsMutation,
    TotadminPermission,
} from 'data/__generated__';

import { TotemInput } from 'components/TotemInput';
import { TotemPopup } from 'components/TotemPopup';
import { TotemPrimaryLoadingButton } from 'components/TotemPrimaryLoadingButton';
import { Loader } from 'components/Loader';

import { CartPanel } from './CartPanel';
import { CatalogueGrid, getStep, type CatalogueProduct } from './CatalogueGrid';
import type { CartLine } from './types';

const PAGE_SIZE = 30;
const SEARCH_DEBOUNCE_MS = 300;

/**
 * La saisie du panier d'une commande, façon catalogue client : recherche et catégories à gauche,
 * panier à droite.
 *
 * Tout se joue en mémoire jusqu'à « Enregistrer », qui envoie le panier COMPLET à
 * `updateOrderProducts` — l'API diffe avec l'existant. Pas de mutation par ligne comme la webapp : ici
 * l'ops corrige souvent plusieurs quantités d'affilée, et un seul aller-retour évite d'avoir à
 * sérialiser les écritures concurrentes.
 */
export const OrderProductsPopup = ({
    order,
    isOpen,
    setIsOpen,
}: {
    order: DetailedOrderFragmentFragment;
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
}) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [categoryId, setCategoryId] = useState<string | null>(null);
    const [quantityByProductId, setQuantityByProductId] = useState<Record<string, number>>({});
    // Les produits déjà commandés n'apparaissent pas forcément dans la page de catalogue affichée :
    // on garde ce qu'on sait d'eux pour rendre leur ligne de panier.
    const [knownProducts, setKnownProducts] = useState<Record<string, CartLine>>({});

    const [updateOrderProducts, { loading: isSaving }] = useUpdateOrderProductsMutation({
        refetchQueries: [GetDetailedOrderDocument],
    });

    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timeout);
    }, [search]);

    // À l'ouverture, le panier repart de la commande en base : rouvrir annule une saisie abandonnée.
    const wasOpen = useRef(false);
    useEffect(() => {
        if (isOpen && !wasOpen.current) {
            setQuantityByProductId(Object.fromEntries(order.products.map(({ _id, quantity }) => [_id, quantity ?? 0])));
            setKnownProducts(
                Object.fromEntries(
                    order.products.map((product) => [
                        product._id,
                        {
                            productId: product._id,
                            name: product.name,
                            flow: product.flow,
                            unitPrice: product.unitPrice ?? 0,
                            quantity: product.quantity ?? 0,
                            step: 1,
                            warning: null,
                        } satisfies CartLine,
                    ]),
                ),
            );
            setSearch('');
            setCategoryId(null);
        }
        wasOpen.current = isOpen;
    }, [isOpen, order.products]);

    const filters = useMemo(
        () => ({
            ...(debouncedSearch ? { search: debouncedSearch } : {}),
            ...(categoryId ? { categoryId } : {}),
        }),
        [debouncedSearch, categoryId],
    );

    const { data: categoriesData } = useGetOrderCatalogueCategoriesQuery({
        variables: { orderId: order._id },
        skip: !isOpen,
    });

    const {
        data: catalogueData,
        loading: catalogueLoading,
        fetchMore,
    } = useGetOrderCatalogueQuery({
        variables: { orderId: order._id, offset: 0, limit: PAGE_SIZE, filters },
        skip: !isOpen,
        notifyOnNetworkStatusChange: true,
    });

    const catalogueProducts = catalogueData?.orderCatalogue.products ?? [];
    const hasMore = catalogueData?.orderCatalogue.hasMore ?? false;

    // Le catalogue affiché est la meilleure source pour prix, colisage et stock : il rafraîchit ce
    // qu'on sait des lignes déjà au panier.
    useEffect(() => {
        if (!catalogueProducts.length) {
            return;
        }
        setKnownProducts((current) => {
            const next = { ...current };
            for (const product of catalogueProducts) {
                next[product._id] = {
                    productId: product._id,
                    name: product.name,
                    flow: product.flow,
                    unitPrice: product.unitPrice,
                    quantity: next[product._id]?.quantity ?? 0,
                    step: getStep(product),
                    warning: buildWarning(product, next[product._id]?.quantity ?? 0),
                };
            }
            return next;
        });
    }, [catalogueProducts]);

    const setQuantity = useCallback((productId: string, quantity: number) => {
        setQuantityByProductId((current) => ({ ...current, [productId]: Math.max(Math.round(quantity), 0) }));
    }, []);

    const addProduct = useCallback(
        (product: CatalogueProduct) => {
            const step = getStep(product);
            setQuantity(product._id, (quantityByProductId[product._id] ?? 0) + step);
        },
        [quantityByProductId, setQuantity],
    );

    const lines: CartLine[] = useMemo(
        () =>
            Object.entries(quantityByProductId)
                .filter(([, quantity]) => quantity > 0)
                .map(([productId, quantity]) => {
                    const known = knownProducts[productId];
                    const catalogueProduct = catalogueProducts.find(({ _id }) => _id === productId);

                    return {
                        productId,
                        name: known?.name ?? productId,
                        flow: known?.flow ?? ('dry' as CartLine['flow']),
                        unitPrice: known?.unitPrice ?? 0,
                        quantity,
                        step: known?.step ?? 1,
                        warning: catalogueProduct ? buildWarning(catalogueProduct, quantity) : null,
                    };
                })
                .sort((a, b) => a.name.localeCompare(b.name)),
        [quantityByProductId, knownProducts, catalogueProducts],
    );

    const handleSave = async () => {
        const { data } = await updateOrderProducts({
            variables: {
                orderId: order._id,
                products: lines.map(({ productId, quantity }) => ({ productId, quantity })),
            },
        });

        if (!data) {
            throw Error("Une erreur inconnue s'est produite");
        }

        const {
            updateOrderProducts: { success, errors, warnings },
        } = data;

        if (!success) {
            errors.forEach((error) => toast.error(error, { autoClose: false }));
            return;
        }

        warnings.forEach((warning) => toast.warning(warning, { autoClose: false }));
        toast.success('Les produits de la commande ont été enregistrés');
        setIsOpen(false);
    };

    return (
        <TotemPopup title="Saisir les produits de la commande" isOpen={isOpen} setIsOpen={setIsOpen}>
            <Layout>
                <CatalogueColumn>
                    <Toolbar>
                        <TotemInput
                            label=""
                            placeholder="Rechercher un produit…"
                            value={search}
                            onChange={setSearch}
                            dataTest="order-catalogue-search"
                        />
                    </Toolbar>
                    <Categories>
                        <CategoryChip type="button" $isActive={!categoryId} onClick={() => setCategoryId(null)}>
                            Toutes
                        </CategoryChip>
                        {(categoriesData?.orderCatalogueCategories ?? [])
                            .slice()
                            .sort((a, b) => a.indexDisplay - b.indexDisplay)
                            .map((category) => (
                                <CategoryChip
                                    key={category._id}
                                    type="button"
                                    $isActive={categoryId === category._id}
                                    onClick={() => setCategoryId(category._id)}
                                >
                                    {category.title}
                                </CategoryChip>
                            ))}
                    </Categories>
                    <CatalogueScroll>
                        {catalogueLoading && !catalogueProducts.length ? (
                            <Loader />
                        ) : (
                            <>
                                <CatalogueGrid
                                    products={catalogueProducts}
                                    quantityByProductId={quantityByProductId}
                                    onAdd={addProduct}
                                />
                                {hasMore ? (
                                    <LoadMore
                                        type="button"
                                        onClick={() =>
                                            fetchMore({
                                                variables: { offset: catalogueProducts.length, limit: PAGE_SIZE },
                                            })
                                        }
                                    >
                                        Charger plus de produits
                                    </LoadMore>
                                ) : null}
                            </>
                        )}
                    </CatalogueScroll>
                </CatalogueColumn>
                <CartPanel
                    lines={lines}
                    minimumOrderAmountInfo={order.totem.minimumOrderAmountInfo}
                    onQuantityChange={setQuantity}
                />
            </Layout>
            <Footer>
                <FooterNote>
                    Les quantités sont enregistrées telles quelles : hors colisage ou au-delà du stock, la commande est
                    créée et un avertissement s’affiche.
                </FooterNote>
                <TotemPrimaryLoadingButton
                    loading={isSaving}
                    onClick={handleSave}
                    data-test="order-products-save"
                    type="button"
                    requiredPermissions={[TotadminPermission.OrdersManage]}
                >
                    Enregistrer
                </TotemPrimaryLoadingButton>
            </Footer>
        </TotemPopup>
    );
};

function buildWarning(product: CatalogueProduct, quantity: number): string | null {
    if (!product.isDeliverableOnDeliveryDate) {
        return 'Non livrable le jour de livraison de la commande';
    }
    if (product.availableStock != null && quantity > product.availableStock) {
        return `Seulement ${product.availableStock} disponibles en entrepôt`;
    }
    const step = getStep(product);
    if (step > 1 && quantity % step !== 0) {
        return `Hors colisage (multiples de ${step})`;
    }
    return null;
}

const Layout = styled.div`
    display: grid;
    grid-template-columns: 1fr 420px;
    gap: 15px;
    height: 65vh;
    min-height: 0;
`;

const CatalogueColumn = styled.div`
    display: flex;
    flex-direction: column;
    min-height: 0;
`;

const Toolbar = styled.div`
    margin-bottom: 10px;
`;

const Categories = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-bottom: 10px;
`;

const CategoryChip = styled.button<{ $isActive: boolean }>`
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 13px;
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    background-color: ${({ theme, $isActive }) => ($isActive ? theme.ctaPrimaryColor : 'transparent')};
    color: ${({ theme, $isActive }) => ($isActive ? theme.ctaPrimaryTextColor : theme.textColor)};
`;

const CatalogueScroll = styled.div`
    flex: 1;
    overflow-y: auto;
    min-height: 0;
    padding-right: 5px;
`;

const LoadMore = styled.button`
    display: block;
    width: 100%;
    margin-top: 10px;
    padding: 8px;
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: ${({ theme }) => theme.borderRadius};
    background: none;
    color: inherit;
    cursor: pointer;
`;

const Footer = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 15px;
    margin-top: 15px;
`;

const FooterNote = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 13px;
`;
